import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Opens an edit dialog when the URL carries `?edit=<id>` — the counterpart to
 *  `useCreateIntent`, and the mechanism that makes modal-only editors
 *  deep-linkable.
 *
 *  Several console resources (notification blocks, themes, language policies,
 *  block lists) are edited in a dialog opened from a row click, so they have no
 *  detail route to link to. Anything that wants to point AT one of those records
 *  — Find & Replace results, a used-by panel, the ⌘K palette — needs a URL that
 *  reopens that dialog, or it can only offer "go to the list and find it
 *  yourself".
 *
 *  The param is consumed immediately (history `replace`, other params kept) so
 *  it doesn't linger in the URL, re-fire on the next render, or add a
 *  back-button step — same contract as `useCreateIntent`.
 *
 *  A non-numeric or absent value is ignored. An id that does not exist (or that
 *  the caller may not see) is the page's own problem: it opens the dialog and
 *  the detail fetch 404s, exactly as a stale row click would.
 */
export function useEditIntent(onOpen: (id: number) => void) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('edit');
  useEffect(() => {
    if (raw == null) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    onOpen(id);
    const next = new URLSearchParams(params);
    next.delete('edit');
    setParams(next, { replace: true });
    // Fire once per appearance of the param; keying on the raw value is what we
    // want (same rationale as useCreateIntent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);
}
